import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import type { RentalDiscountApplyResult } from '@kgc/api-contract';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { applyItemDiscounts, type RentalItem } from '../../../api/rentals';
import { formatCurrency } from '../mock-data';

/**
 * ADR-037 Am-1 — per-gép manuális engedmény UI (post-creation, RentalDetailPage).
 *
 * NARROW scope (Q1 2026-06-07, Javo): a per-gép engedmény **role-cap-only**
 * (clamp-and-flag) — a partner-kategorizált 15% érintetlen. A BE
 * (`POST /rentals/:id/item-discounts`, #312) clamp-eli a role-cap-re és a
 * `cappedByRole` flaggel jelzi; itt ezt LÁTHATÓAN mutatjuk (nem hibaüzenet).
 *
 * Csak `DRAFT`/`CONFIRMED` rentalon renderelődik (a hívó gate-eli).
 */
export function RentalItemDiscountSection({
  rentalId,
  items,
  onApplied,
}: {
  rentalId: string;
  items: RentalItem[];
  onApplied?: () => void;
}) {
  const { t } = useTranslation();
  const [pctById, setPctById] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RentalDiscountApplyResult | null>(null);

  const resultById = new Map((result?.items ?? []).map(r => [r.rentalItemId, r]));

  const handleApply = async (): Promise<void> => {
    const payload = items
      .map(item => ({ rentalItemId: item.id, discountPct: Number(pctById[item.id] ?? '') }))
      .filter(d => Number.isFinite(d.discountPct) && d.discountPct > 0);
    if (payload.length === 0) {
      setError(t('rental.itemDiscount.noneEntered'));
      return;
    }
    setBusy(true);
    setError(null);
    // A korábbi eredmény törlése re-apply-kor — különben a busy alatt a régi
    // (stale) per-item összegek + totals látszanak, ami pénzügyi adatként félrevezet.
    setResult(null);
    try {
      const res = await applyItemDiscounts(rentalId, payload);
      setResult(res.data);
      onApplied?.();
    } catch {
      setError(t('rental.itemDiscount.applyFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="rental-item-discount-section">
      <CardHeader>
        <CardTitle>{t('rental.itemDiscount.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">{t('rental.itemDiscount.helper')}</p>

        <div className="space-y-3">
          {items.map(item => {
            const r = resultById.get(item.id);
            return (
              <div
                key={item.id}
                className="flex flex-wrap items-center gap-3 rounded-lg border p-3 dark:border-slate-600"
                data-testid={`discount-row-${item.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-foreground">{item.equipmentName}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('rental.totalRentalFee')}: {formatCurrency(item.itemTotal)}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={pctById[item.id] ?? ''}
                    onChange={e => setPctById(prev => ({ ...prev, [item.id]: e.target.value }))}
                    className="w-20"
                    aria-label={t('rental.itemDiscount.inputAria', { name: item.equipmentName })}
                    data-testid={`discount-input-${item.id}`}
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
                {r && (
                  <div
                    className="w-full text-right text-xs"
                    data-testid={`discount-result-${item.id}`}
                  >
                    <span className="text-foreground">
                      {t('rental.itemDiscount.applied', { pct: r.appliedPct })} ·{' '}
                      {formatCurrency(r.discountAmount)}
                    </span>
                    {r.cappedByRole && (
                      <span
                        className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                        data-testid={`discount-capped-${item.id}`}
                      >
                        {t('rental.itemDiscount.cappedByRole', { pct: r.appliedPct })}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {error && (
          <p
            className="text-sm text-red-600 dark:text-red-400"
            data-testid="discount-error"
            role="alert"
          >
            {error}
          </p>
        )}

        {result && (
          <dl
            className="space-y-1 rounded-lg bg-gray-50 p-3 text-sm dark:bg-slate-800"
            data-testid="discount-result-totals"
          >
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{t('rental.itemDiscount.totalDiscount')}</dt>
              <dd className="font-medium text-foreground">
                {formatCurrency(result.totalDiscountAmount)}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{t('rental.itemDiscount.finalAmount')}</dt>
              <dd className="font-bold text-primary">{formatCurrency(result.finalAmount)}</dd>
            </div>
          </dl>
        )}

        <Button
          type="button"
          disabled={busy}
          onClick={() => void handleApply()}
          data-testid="apply-discounts-btn"
        >
          {t('rental.itemDiscount.apply')}
        </Button>
      </CardContent>
    </Card>
  );
}
